// src/components/admin/DropdownAdmin.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2, Loader, RefreshCw, AlertCircle } from 'lucide-react';
import dropdownService from '../../services/dropDownService';

const DropdownAdmin = () => {
  // États pour gérer les données
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [values, setValues] = useState([]);

  // États pour le modal d'ajout/édition de valeur
  const [showModal, setShowModal] = useState(false);
  const [currentValue, setCurrentValue] = useState({ id: '', label: '', value: '', dropdownCategoryId: '' });
  const [isEditing, setIsEditing] = useState(false);

  // États pour gérer le chargement et les erreurs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Charger toutes les catégories au chargement
  useEffect(() => {
    fetchCategories();
  }, []);

  // Charger les valeurs quand une catégorie est sélectionnée
  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryValues(selectedCategory);
    } else {
      setValues([]);
    }
  }, [selectedCategory]);

  // Récupérer toutes les catégories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await dropdownService.getAllCategories();
      setCategories(data);
      setError(null);
    } catch (err) {
      console.error("Erreur lors du chargement des catégories", err);
      setError("Impossible de charger les catégories. Veuillez réessayer.");
      toast.error("Erreur lors du chargement des catégories");
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les valeurs d'une catégorie spécifique
  const fetchCategoryValues = async (categoryId) => {
    try {
      setLoading(true);
      const category = await dropdownService.getCategoryById(categoryId);
      setValues(category.values || []);
      setError(null);
    } catch (err) {
      console.error(`Erreur lors du chargement des valeurs pour ${categoryId}`, err);
      setError("Impossible de charger les valeurs. Veuillez réessayer.");
      toast.error("Erreur lors du chargement des valeurs");
      setValues([]);
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir le modal pour ajouter une nouvelle valeur
  const handleAddValue = () => {
    setCurrentValue({ id: '', label: '', value: '', dropdownCategoryId: selectedCategory });
    setIsEditing(false);
    setShowModal(true);
  };

  // Ouvrir le modal pour éditer une valeur existante
  const handleEditValue = (value) => {
    setCurrentValue({ ...value, dropdownCategoryId: selectedCategory });
    setIsEditing(true);
    setShowModal(true);
  };

  // Gérer la soumission du formulaire d'ajout/édition
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      if (isEditing) {
        // Mettre à jour une valeur existante
        await dropdownService.updateValue(currentValue.id, currentValue);
        toast.success("Valeur mise à jour avec succès");
      } else {
        // Ajouter une nouvelle valeur
        await dropdownService.addValue(selectedCategory, {
          label: currentValue.label,
          value: currentValue.value
        });
        toast.success("Valeur ajoutée avec succès");
      }

      // Recharger les valeurs
      fetchCategoryValues(selectedCategory);

      // Fermer le modal
      setShowModal(false);

    } catch (err) {
      console.error("Erreur lors de l'enregistrement", err);
      toast.error("Erreur lors de l'enregistrement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une valeur
  const handleDeleteValue = async (valueId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette valeur ?")) {
      return;
    }

    try {
      setLoading(true);
      await dropdownService.deleteValue(valueId);
      toast.success("Valeur supprimée avec succès");

      // Recharger les valeurs
      fetchCategoryValues(selectedCategory);
    } catch (err) {
      console.error("Erreur lors de la suppression", err);
      toast.error("Erreur lors de la suppression. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Gérer les changements de champs dans le modal
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentValue(prev => ({ ...prev, [name]: value }));
  };

  // Fermer le modal
  const closeModal = () => {
    setShowModal(false);
  };

  // Actualiser les données
  const handleRefresh = () => {
    if (selectedCategory) {
      fetchCategoryValues(selectedCategory);
    } else {
      fetchCategories();
    }
  };

  // Trouver le nom de la catégorie sélectionnée
  const selectedCategoryName = selectedCategory
    ? categories.find(c => c.id === selectedCategory)?.name || "Catégorie"
    : "Catégorie";

  return (
    <div className="dropdown-admin">
      <div className="admin-header">
        <h2 className="admin-header-title">Gestion des listes déroulantes</h2>
        <button
          className="btn-refresh"
          onClick={handleRefresh}
          title="Rafraîchir les données"
        >
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      {error && (
        <div className="admin-alert">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="admin-container">
        <div className="admin-sidebar-container">
          <div className="admin-section">
            <h3 className="admin-section-title">Catégories</h3>
            <div className="admin-section-content">
              <div className="form-group">
                <select
                  className="form-select"
                  value={selectedCategory || ''}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Sélectionnez une catégorie</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="admin-content-container">
          <div className="admin-section">
            <div className="admin-section-header">
              <h3 className="admin-section-title">
                {selectedCategory ? `Valeurs pour ${selectedCategoryName}` : 'Valeurs'}
              </h3>
              {selectedCategory && (
                <button
                  className="btn-primary"
                  onClick={handleAddValue}
                  disabled={loading}
                >
                  <Plus size={16} /> Ajouter
                </button>
              )}
            </div>

            <div className="admin-section-content">
              {loading ? (
                <div className="admin-loading">
                  <Loader size={24} className="spinning" />
                  <p>Chargement en cours...</p>
                </div>
              ) : !selectedCategory ? (
                <div className="admin-info">
                  Veuillez sélectionner une catégorie pour afficher et gérer ses valeurs.
                </div>
              ) : values.length === 0 ? (
                <div className="admin-info">
                  Aucune valeur trouvée pour cette catégorie. Cliquez sur "Ajouter" pour en créer une.
                </div>
              ) : (
                <div className="admin-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Libellé</th>
                        <th>Valeur</th>
                        <th className="actions-column">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {values.map(value => (
                        <tr key={value.id}>
                          <td>{value.label}</td>
                          <td><code>{value.value}</code></td>
                          <td className="actions-column">
                            <div className="actions-buttons">
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleEditValue(value)}
                                title="Modifier"
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => handleDeleteValue(value.id)}
                                title="Supprimer"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal pour ajouter/éditer une valeur */}
      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h4 className="admin-modal-title">
                {isEditing
                  ? `Modifier une valeur de ${selectedCategoryName}`
                  : `Ajouter une valeur à ${selectedCategoryName}`}
              </h4>
              <button
                className="btn-close"
                onClick={closeModal}
                aria-label="Fermer"
              >&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-modal-body">
                <div className="form-group">
                  <label htmlFor="label" className="form-label">Libellé</label>
                  <input
                    type="text"
                    className="form-control"
                    id="label"
                    name="label"
                    value={currentValue.label}
                    onChange={handleInputChange}
                    required
                    placeholder="Libellé affiché à l'utilisateur"
                  />
                  <div className="form-text">
                    Le texte qui sera affiché dans la liste déroulante.
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="value" className="form-label">Valeur</label>
                  <input
                    type="text"
                    className="form-control"
                    id="value"
                    name="value"
                    value={currentValue.value}
                    onChange={handleInputChange}
                    required
                    placeholder="Valeur enregistrée"
                  />
                  <div className="form-text">
                    La valeur qui sera enregistrée dans la base de données.
                  </div>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader size={16} className="spinning me-2" />
                      Traitement...
                    </>
                  ) : (
                    'Enregistrer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx="true">{`
        /* Variables de couleurs basées sur le logo */
        :root {
          --ic-blue: #0a6ebd;
          --ic-orange: #d35400;
          --ic-light-orange: #f39c12;
          --ic-gray: #777;
          --ic-light-gray: #f8f9fa;
          --ic-white: #fff;
        }

        .dropdown-admin {
          width: 100%;
        }

        .admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .admin-header-title {
          font-size: 1.5rem;
          margin: 0;
          color: var(--ic-blue);
        }

        .btn-refresh {
          background: none;
          border: none;
          color: var(--ic-gray);
          cursor: pointer;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .btn-refresh:hover {
          background-color: rgba(10, 110, 189, 0.1);
          color: var(--ic-blue);
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .admin-alert {
          display: flex;
          align-items: center;
          padding: 15px;
          background-color: #fee;
          color: #d63031;
          border-radius: 5px;
          margin-bottom: 20px;
        }

        .admin-alert svg {
          margin-right: 10px;
          flex-shrink: 0;
        }

        .admin-container {
          display: flex;
          gap: 20px;
        }

        .admin-sidebar-container {
          width: 300px;
          flex-shrink: 0;
        }

        .admin-content-container {
          flex: 1;
        }

        .admin-section {
          background-color: var(--ic-white);
          border-radius: 8px;
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
          margin-bottom: 20px;
          overflow: hidden;
        }

        .admin-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          background-color: var(--ic-light-gray);
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .admin-section-title {
          font-size: 1.1rem;
          margin: 0;
          color: var(--ic-blue);
        }

        .admin-section-content {
          padding: 20px;
        }

        /* Form elements */
        .form-group {
          margin-bottom: 15px;
        }

        .form-label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
          color: #333;
        }

        .form-select, .form-control {
          width: 100%;
          padding: 10px;
          border: 1px solid rgba(0, 0, 0, 0.15);
          border-radius: 4px;
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .form-select:focus, .form-control:focus {
          border-color: var(--ic-blue);
          outline: none;
          box-shadow: 0 0 0 3px rgba(10, 110, 189, 0.15);
        }

        .form-text {
          margin-top: 5px;
          font-size: 12px;
          color: var(--ic-gray);
        }

        /* Buttons */
        .btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          background-color: var(--ic-blue);
          color: white;
          border: none;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-primary:hover {
          background-color: #085494;
        }

        .btn-primary:disabled {
          background-color: var(--ic-gray);
          cursor: not-allowed;
        }

        .btn-primary svg {
          margin-right: 5px;
        }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          background-color: #f1f2f6;
          color: #333;
          border: none;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
        }

        .btn-secondary:hover {
          background-color: #dfe4ea;
        }

        /* Icon buttons */
        .btn-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-edit {
          background-color: rgba(10, 110, 189, 0.1);
          color: var(--ic-blue);
        }

        .btn-edit:hover {
          background-color: rgba(10, 110, 189, 0.2);
        }

        .btn-delete {
          background-color: rgba(211, 84, 0, 0.1);
          color: var(--ic-orange);
        }

        .btn-delete:hover {
          background-color: rgba(211, 84, 0, 0.2);
        }

        /* Loading indicator */
        .admin-loading {
          padding: 40px 0;
          text-align: center;
          color: var(--ic-gray);
        }

        .admin-loading p {
          margin-top: 10px;
        }

        /* Info messages */
        .admin-info {
          padding: 20px;
          text-align: center;
          background-color: rgba(10, 110, 189, 0.05);
          color: var(--ic-blue);
          border-radius: 4px;
        }

        /* Table */
        .admin-table {
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th, td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        th {
          font-weight: 600;
          color: var(--ic-gray);
          background-color: var(--ic-light-gray);
        }

        td {
          color: #333;
        }

        tr:hover {
          background-color: rgba(10, 110, 189, 0.02);
        }

        .actions-column {
          width: 100px;
          text-align: center;
        }

        .actions-buttons {
          display: flex;
          justify-content: center;
          gap: 5px;
        }

        code {
          padding: 2px 5px;
          background-color: var(--ic-light-gray);
          border-radius: 3px;
          font-family: monospace;
          font-size: 13px;
        }

        /* Modal */
        .admin-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
        }

        .admin-modal {
          width: 500px;
          max-width: 90%;
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          overflow: hidden;
        }

        .admin-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          background-color: var(--ic-light-gray);
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }

        .admin-modal-title {
          margin: 0;
          font-size: 1.2rem;
          color: var(--ic-blue);
        }

        .btn-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: var(--ic-gray);
          cursor: pointer;
        }

        .admin-modal-body {
          padding: 20px;
        }

        .admin-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 15px 20px;
          background-color: var(--ic-light-gray);
          border-top: 1px solid rgba(0, 0, 0, 0.1);
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .admin-container {
            flex-direction: column;
          }

          .admin-sidebar-container {
            width: 100%;
          }
        }

        /* Add a little extra space to the right of the spinning icon */
        .me-2 {
          margin-right: 8px;
        }
      `}</style>
    </div>
  );
};

export default DropdownAdmin;